import {Menu, MenuItem, useTheme} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import {ExclamationCircleOutlined, LoadingOutlined, StarOutlined, StarTwoTone} from "@ant-design/icons";
import * as React from "react";
import {MoreVert} from "@mui/icons-material";
import {HeadCellConfig} from "./EnhancedTableHeaderRow";
import Box from "@mui/material/Box";
import {ReactComponent as NoDataSvg} from "../../assets/no_data.svg";
import Typography from "@mui/material/Typography";


interface RowMenuProps {
    rowData: any;
    canEdit?: boolean;
    canDelete?: boolean;
    editHandler?: Function;
    deleteHandler?: Function;
}

const RowMenu = (props: RowMenuProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleEdit = (e) => {
        props.editHandler(props.rowData)
        setAnchorEl(null);
    }

    const handleDelete = (e) => {
        props.deleteHandler(props.rowData)
        setAnchorEl(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <React.Fragment>
            <IconButton
                sx={{mr: 2}}
                size="small"
                aria-label="edit row"
                aria-controls="row-edit"
                aria-haspopup="true"
                onClick={handleClick}
                color="inherit">
                <MoreVert fontSize="small"/>
            </IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {props.canEdit &&
                    <MenuItem onClick={handleEdit}>Edit</MenuItem>
                }
                {props.canDelete &&
                    <MenuItem onClick={handleDelete}>Delete</MenuItem>
                }
            </Menu>
        </React.Fragment>
    )
}

interface EmptyRowsProps {
    count: number;
    colSpan: number;
    borderColor?: string;
    hideBorder?: boolean;
}

export const EmptyRows = (props: EmptyRowsProps) => {
    return (
        <React.Fragment>
            {
                Array(props.count)
                    .fill(null)
                    .map((_, i) => {
                        const sx = {};
                        if (props.borderColor) {
                            sx['borderColor'] = props.borderColor;
                        } else if (props.hideBorder && props.count !== i + 1) {
                            sx['borderColor'] = 'transparent';
                        }
                        return (
                            <TableRow key={i}>
                                <TableCell colSpan={props.colSpan} sx={sx}>&nbsp;</TableCell>
                            </TableRow>
                        )

                    })
            }
        </React.Fragment>
    )
}


interface PlaceholderContainerProps {
    pageSize: number;
    colSpan: number;
    children: React.ReactNode;
}

const PlaceholderContainer = (props: PlaceholderContainerProps) => {
    const emptyRows = props.pageSize >= 10 ? props.pageSize - 5 : 5;
    return (
        <React.Fragment>
            <EmptyRows count={2} colSpan={props.colSpan} borderColor="transparent"/>
            <TableRow>
                <TableCell colSpan={props.colSpan} sx={{textAlign: "center", p: 0, borderBottom: 'none'}}>
                    <Box sx={{height: '159px'}}>
                        {props.children}
                    </Box>
                </TableCell>
            </TableRow>
            <EmptyRows count={emptyRows} colSpan={props.colSpan} hideBorder/>
        </React.Fragment>
    )
}

interface EnhancedTablBodyProps {
    loadingState: 'loading' | 'error' | 'success';
    columns: HeadCellConfig[];
    rows: object[];
    pageSize: number;
    headers: HeadCellConfig[];
    canEdit?: boolean;
    canDelete?: boolean;
    canFavorite?: boolean;
    editHandler?: Function;
    deleteHandler?: Function;
    favoriteHandler?: Function;
}

export default (props: EnhancedTablBodyProps) => {
    const theme = useTheme();

    const colSpan = props.canEdit ? Object.keys(props.headers).length + 1 : Object.keys(props.headers).length;
    const rowStyle = {
        '&.MuiTableRow-root:hover': {
            backgroundColor: '#e7f0fa'
        }
    };

    switch (props.loadingState) {
        case 'success':
            const rowData = props.rows;

            return (
                <TableBody>
                    {
                        rowData.length === 0 && (
                            <PlaceholderContainer pageSize={props.pageSize} colSpan={colSpan}>
                                <Box component={NoDataSvg} sx={{display: 'flex', margin: 'auto', height: '85px'}}/>
                                <Typography variant="body1">
                                    No Data
                                </Typography>
                            </PlaceholderContainer>
                        )
                    }
                    {
                        rowData.map((row: any, i: number) => {
                            const key = `${row}_${i}`
                            return (
                                <TableRow sx={rowStyle} key={key} hover>
                                    {
                                        props.columns.map(colCfg => {
                                            // Set a default formatter if none is provided by the col config.
                                            const formatter = colCfg.formatter ? colCfg.formatter : (row) => row[colCfg.key];
                                            if (colCfg.key === 'favorite') {
                                                return (
                                                    <TableCell
                                                        key={`${key}_${colCfg.key}`}
                                                        align="center"
                                                        sx={{width: '.5rem'}}
                                                    >
                                                        <IconButton
                                                            sx={{p: 0}}
                                                            size='small'
                                                            onClick={() => props.favoriteHandler(row)}>
                                                            {!row[colCfg.key] && (
                                                                <StarOutlined
                                                                    style={{'fontSize': '1rem', 'opacity': '54%'}}/>
                                                            )}
                                                            {row[colCfg.key] && (
                                                                <StarTwoTone
                                                                    twoToneColor={theme.palette.primary.main}
                                                                    style={{'fontSize': '1rem'}}/>
                                                            )}
                                                        </IconButton>
                                                    </TableCell>
                                                )
                                            }

                                            const displayVal = formatter(row);
                                            return (
                                                <TableCell key={`${key}_${colCfg.key}`} align="left">
                                                    {displayVal ? displayVal : null}
                                                </TableCell>
                                            )
                                        })

                                    }
                                    {
                                        (props.canEdit || props.canDelete) && (
                                            <TableCell align="right" padding="checkbox">
                                                <RowMenu rowData={row} canEdit={props.canEdit}
                                                         canDelete={props.canDelete} editHandler={props.editHandler}
                                                         deleteHandler={props.deleteHandler}/>
                                            </TableCell>
                                        )
                                    }
                                </TableRow>
                            );
                        })
                    }
                    {
                        // Only fill when there is data.
                        (rowData.length !== 0 &&  props.pageSize - props.rows.length > 0) &&
                            <EmptyRows count={props.pageSize - props.rows.length} colSpan={colSpan}/>
                    }
                </TableBody>
            )
        case 'loading':
            return (
                <TableBody>
                    <PlaceholderContainer pageSize={props.pageSize} colSpan={colSpan}>
                        <Box>
                            <LoadingOutlined
                                style={{fontSize: '50px', color: '#aeb8c2', marginTop: '20px', marginBottom: '15px'}}/>
                        </Box>
                        <Typography variant="body1">
                            Loading
                        </Typography>
                    </PlaceholderContainer>
                </TableBody>
            )
        case 'error':
            return (
                <TableBody>
                    <PlaceholderContainer pageSize={props.pageSize} colSpan={colSpan}>
                        <Box>
                            <ExclamationCircleOutlined
                                style={{fontSize: '70px', color: '#aeb8c2', marginBottom: '15px'}}/>
                        </Box>
                        <Typography variant="body1">
                            Error
                        </Typography>
                    </PlaceholderContainer>
                </TableBody>
            )
    }
}
